import type { ColumnsType, AnyObject } from "@similarweb/pro-logic-components";
import { TextAnchorCell } from "@similarweb/pro-logic-components";
import { ChangeValue, PlainTooltip, Type } from "@similarweb/ui-components";

import { NO_DATA_SIGN } from "app/filters/const";
import { allTrackers } from "app/services/track/track";
import { translate } from "app/filters/i18nFilter";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { channelsToShow, totalChangeRow } from "../consts";

import { CellWithLeftPadding, DomainIconContainer, ValueCell, StyledItemIcon } from "./styledComponent";

const getChannelByLabel = (label: string) => {
    return channelsToShow.find((channel) => channel.label === label);
};

const getHref = (state: string, key: string) => {
    const baseParams = proNavigator.getParams();
    return proNavigator.href(state, {
        ...baseParams,
        key,
        isWWW: "*",
    });
};

const renderChannelCell = (value: string, record: AnyObject, eventName: string, keyParam: string) => {
    const channel = getChannelByLabel(record.channel);
    const { state, value: channelValue } = channel || {};

    if (!state) {
        return <CellWithLeftPadding>{translate(value)}</CellWithLeftPadding>;
    }

    const href = getHref(state, keyParam);

    const handleClick = () =>
        allTrackers.dispatchTrackEvent("Internal Link", "click", `${eventName}/${channelValue}/compare`);

    return (
        <CellWithLeftPadding>
            <TextAnchorCell
                href={href}
                onClick={handleClick}
            >
                {translate(value)}
            </TextAnchorCell>
        </CellWithLeftPadding>
    );
};

const renderDomainCell = (
    domainName: string,
    value: number,
    record: AnyObject,
    formatter: (val: number, digits: number) => string,
    eventName: string,
) => {
    const isTotalRow = record.channel === totalChangeRow.channel;
    const channel = getChannelByLabel(record.channel);
    const { state, value: channelValue } = channel || {};
    if (isTotalRow) {
        const formatted = formatter(value, 2);
        return (
            <ChangeValue
                value={formatted}
                isDecrease={value < 0}
            />
        );
    }

    const href = state ? getHref(state, domainName) : undefined;

    const handleClick = () =>
        allTrackers.dispatchTrackEvent("Internal Link", "click", `${eventName}/${channelValue}/single`);

    return (
        <ValueCell
            isClickable={!!state}
            href={href}
            onClick={state ? handleClick : undefined}
        >
            {value}
        </ValueCell>
    );
};

export const getTableColumns = (
    domains: { name: string; favicon: string | null }[],
    formatter: (val: number, digits: number) => string,
    eventName: string,
    withOpacity = false,
): ColumnsType<AnyObject> => {
    const { key: keyParam } = proNavigator.getParams();

    const channelColumn = {
        title: <CellWithLeftPadding>{translate("mmx.channel")}</CellWithLeftPadding>,
        dataIndex: "channel",
        width: 134,
        maxWidth: 134,
        fixed: "left",
        ellipsis: false,
        disableHeatmap: true,
        resize: false,
        render: (value: string, record: AnyObject) => renderChannelCell(value, record, eventName, keyParam),
    };

    const domainColumns = domains.map(({ name, favicon }, index) => ({
        title: () =>
            name ? (
                <PlainTooltip tooltipContent={name}>
                    <DomainIconContainer>
                        <StyledItemIcon
                            iconType={Type.Website}
                            iconSrc={favicon}
                        />
                    </DomainIconContainer>
                </PlainTooltip>
            ) : (
                NO_DATA_SIGN
            ),
        dataIndex: name,
        resize: false,
        opacity: index === 0 ? 1 : withOpacity && 0.4,
        render: (value: number, record: AnyObject) => renderDomainCell(name, value, record, formatter, eventName),
    }));

    return [channelColumn, ...domainColumns] as ColumnsType<AnyObject>;
};
